import type { LomavisGrowthCardProps } from '@/lomavisSharedUi/organisms/cards/LomavisGrowthCard';
import { adLevel, UNIT_TYPE_CURRENCY, UNIT_TYPE_PERCENT } from './growthConfig';
import type {
  PaidAd,
  PaidAdset,
  PaidCampaign
} from '@/lomavisSharedUi/templates/sections/LomavisPaidAnalyticsView1/LomavisPaidAnalyticsView1';
import {
  DEFAULT_CURRENCY,
  getCurrencySymbol,
  type SupportedCurrency
} from './currency';

const toNumber = (v: unknown): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') {
    const n = Number(v.replace(/[, ]+/g, ''));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

type UnitType = typeof UNIT_TYPE_CURRENCY | typeof UNIT_TYPE_PERCENT | 'none';

export type InsightMetricConfig = {
  key: string;
  title: string;
  helpText?: string;
  cssIcon?: string;
  cssIconColor?: string;
  unitType?: UnitType;
  aggregation?: 'sum' | 'average';
};

export type InsightSummary = {
  totalMetrics: Record<string, number>;
  averageMetrics: Record<string, number>;
  campaignCount: number;
};
export type EntityLevel = (typeof adLevel)[keyof typeof adLevel];

export const summarizeCampaignInsights = (
  rawDataInput: any[],
  metricsInput?: InsightMetricConfig[]
): InsightSummary => {
  const rawData = Array.isArray(rawDataInput) ? rawDataInput : [];
  const metrics = Array.isArray(metricsInput) ? metricsInput : [];

  if (metrics.length === 0 || rawData.length === 0) {
    return {
      totalMetrics: {},
      averageMetrics: {},
      campaignCount: rawData.length
    };
  }

  const totals: Record<string, number> = {};

  metrics.forEach((metric) => {
    const values: number[] = [];

    rawData.forEach((item: any) => {
      const insights = item?.insights || item?.data?.insights;
      if (!insights || typeof insights !== 'object') return;
      const num = toNumber((insights as any)[metric.key]);
      if (Number.isFinite(num)) values.push(num);
    });

    if (values.length === 0) return;

    const sum = values.reduce((acc, val) => acc + val, 0);
    const aggregation = metric.aggregation || 'sum';

    if (aggregation === 'average') {
      totals[metric.key] = Number((sum / values.length).toFixed(2));
    } else {
      totals[metric.key] = sum;
    }
  });

  const count = rawData.length;
  const averages: Record<string, number> = {};
  metrics.forEach((metric) => {
    const val = totals[metric.key];
    if (val === undefined) return;
    const aggregation = metric.aggregation || 'sum';
    averages[metric.key] =
      aggregation === 'average'
        ? val
        : count > 0
        ? Number((val / count).toFixed(2))
        : 0;
  });

  return {
    totalMetrics: totals,
    averageMetrics: averages,
    campaignCount: count
  };
};

const resolveUnitSymbol = (
  cfg: InsightMetricConfig | undefined,
  currencySymbol: string
): string => {
  if (cfg?.unitType === UNIT_TYPE_CURRENCY) return currencySymbol;
  if (cfg?.unitType === UNIT_TYPE_PERCENT) return '%';
  return '';
};

export const buildGrowthCardEntries = (
  metrics: Record<string, number>,
  configs: InsightMetricConfig[],
  t: any,
  currency: SupportedCurrency = DEFAULT_CURRENCY
): LomavisGrowthCardProps[] => {
  const currencySymbol = getCurrencySymbol(currency);

  return configs
    .filter((cfg) => metrics[cfg.key] !== undefined)
    .map((cfg, idx) => ({
      title: `${t(`adsAnalytics.${cfg.key}`)}`,
      helpText: `${t(`adsAnalytics.tooltip.${cfg.key}`)}`,
      cssIcon: cfg.cssIcon || 'fa-solid fa-chart-line',
      cssIconColor: cfg.cssIconColor,
      dataUnit: resolveUnitSymbol(cfg, currencySymbol),
      current: Number.isFinite(metrics[cfg.key]) ? metrics[cfg.key] : 0,
      previous: 0,
      showGrowthBadge: false
    }));
};

const getInsightValue = (insights: any, key: string): number => {
  if (!insights || typeof insights !== 'object') return 0;
  return toNumber(insights[key]);
};

type AdsetWithCampaign = PaidAdset & {
  campaignId?: string;
  campaignName?: string;
};

type AdWithAdset = PaidAd & {
  adsetId?: string;
  adsetName?: string;
};

const flattenAdsets = (campaigns: PaidCampaign[]): AdsetWithCampaign[] =>
  campaigns.flatMap((c) =>
    (c.adsets ?? []).map((as) => ({
      ...as,
      campaignId: c.id,
      campaignName: c.name
    }))
  );

const flattenAds = (adsets: AdsetWithCampaign[]): AdWithAdset[] =>
  adsets.flatMap((as) =>
    (as.ads ?? []).map((ad) => ({
      ...ad,
      adsetId: as.id,
      adsetName: as.name
    }))
  );

export type PerformerItem = {
  id: string;
  title: string;
  subtitle?: string;
  value: number;
  image?: string;
};

export const buildBestPerformers = (
  campaigns: PaidCampaign[],
  level: EntityLevel,
  metricKey: string,
  limit = 5
): PerformerItem[] => {
  const adsets = flattenAdsets(campaigns);
  const ads = flattenAds(adsets);

  let rows: Array<{
    id: string;
    name: string;
    insights?: any;
    parentName?: string;
    preview_image_url?: string;
  }> = campaigns.map((c) => ({
    id: c.id,
    name: c.name,
    insights: c.insights,
    preview_image_url: c.preview_image_url
  }));

  if (level === adLevel.ADSET) {
    rows = adsets.map((as) => ({
      id: as.id,
      name: as.name,
      insights: as.insights,
      parentName: as.campaignName,
      preview_image_url: as.preview_image_url
    }));
  } else if (level === adLevel.ADS) {
    rows = ads.map((ad: any) => ({
      id: ad.id,
      name: ad.name,
      insights: ad.insights,
      parentName: ad.adsetName,
      preview_image_url: ad.preview_image_url
    }));
  }

  return rows
    .map((r) => ({
      id: r.id,
      title: r.name || '-',
      subtitle: r.parentName,
      value: getInsightValue(r.insights, metricKey),
      image: r.preview_image_url
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
};

export const buildBestPerformerCards = (
  campaignsInput: PaidCampaign[] | undefined,
  level: EntityLevel,
  metricsInput?: InsightMetricConfig[],
  currency: SupportedCurrency = DEFAULT_CURRENCY
) => {
  const campaigns = Array.isArray(campaignsInput) ? campaignsInput : [];
  const metrics = Array.isArray(metricsInput) ? metricsInput : [];
  if (metrics.length === 0 || campaigns.length === 0) return [];

  const adsets = flattenAdsets(campaigns);
  const ads = flattenAds(adsets);

  let rows: Array<{
    id: string;
    name: string;
    insights?: any;
    parentName?: string;
    preview_image_url?: string;
  }> = campaigns.map((c) => ({
    id: c.id,
    name: c.name,
    insights: c.insights,
    preview_image_url: c.preview_image_url
  }));

  if (level === adLevel.ADSET) {
    rows = adsets.map((as) => ({
      id: as.id,
      name: as.name,
      insights: as.insights,
      parentName: as.campaignName,
      preview_image_url: as.preview_image_url
    }));
  } else if (level === adLevel.ADS) {
    rows = ads.map((ad: any) => ({
      id: ad.id,
      name: ad.name,
      insights: ad.insights,
      parentName: ad.adsetName,
      preview_image_url: ad.preview_image_url
    }));
  }

  const currencySymbol = getCurrencySymbol(currency);

  return rows.map((r) => ({
    preview_image_url: r.preview_image_url,
    text: r.name || '-',
    analytics: (() => {
      const insights = r.insights || {};
      const metricsByKey = metrics.reduce(
        (acc, cfg) => ({ ...acc, [cfg.key]: cfg }),
        {} as Record<string, InsightMetricConfig | undefined>
      );
      const ordered: Record<string, { value: number | string; unit?: string }> =
        {};

      const pushMetric = (key: string, raw: any, unit?: string) => {
        const n = toNumber(raw);
        ordered[key] = Number.isFinite(n)
          ? { value: n, unit }
          : { value: raw as string | number, unit };
      };

      metrics.forEach((cfg) => {
        const unit = resolveUnitSymbol(cfg, currencySymbol);
        if (Object.prototype.hasOwnProperty.call(insights, cfg.key)) {
          pushMetric(cfg.key, insights[cfg.key], unit);
          return;
        }
        const n = getInsightValue(insights, cfg.key);
        ordered[cfg.key] = {
          value: Number.isFinite(n) ? n : n ?? '-',
          unit
        };
      });

      const extras = Object.keys(insights)
        .filter((key) => !Object.prototype.hasOwnProperty.call(ordered, key))
        .sort();

      extras.forEach((key) =>
        pushMetric(
          key,
          insights[key],
          resolveUnitSymbol(metricsByKey[key], currencySymbol)
        )
      );

      return ordered;
    })(),
    profile: r.parentName ? { username: r.parentName } : {}
  }));
};
